// ============================================
// use-yjs-connection.ts — R14/R16 Yjs WebSocket lifecycle
// ============================================
//
// Owns the WebSocketProvider lifecycle for a single DOCUMENT view:
//
//   1. Mount: opens a y-websocket connection to the configured
//      endpoint (`/yws/<roomName>` via the nginx proxy, or
//      `ws://localhost:1234/<roomName>` in dev).
//   2. Awareness: subscribes to presence (cursor + display name).
//   3. Unmount: tears the connection down so the WS handshake isn't
//      leaked across tab switches.
//
// Returns the Y.Doc + provider so the TipTap editor can bind them via
// @tiptap/extension-collaboration. The caller is responsible for
// hydrating the Y.Doc with the persisted snapshot — see
// `DocumentEditor` for the hydration + provider wiring.
//
// IMPORTANT (R16): we pass ONLY the server base URL to the
// WebsocketProvider. y-websocket 2.1.0's `WebsocketProvider` (see
// node_modules/y-websocket/src/y-websocket.js line ~403) builds the
// final WS URL as `${serverUrl}/${roomname}`. If we pre-bake the
// roomname into the URL, the final path becomes
// `/yws/<room>/<room>` — nginx would forward the nested path to
// ywebsocket, the ywebsocket server would try to load a doc with a
// `/` in its name (which fails silently, no doc is created, and the
// client sees a "disconnected" status). The correct shape is to
// hand the provider `wss://host/yws` and let it append the room.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';

// ============================================
// TYPES
// ============================================

export interface YjsConnectionPeer {
    /** ClientID assigned by y-websocket. */
    clientId: number;
    /** Display name the peer set on its awareness state. */
    name: string;
    /** Hex colour for the cursor (mirrors the y-websocket extension default). */
    color: string;
}

export interface YjsConnectionStatus {
    /** Raw WebSocket readyState (CONNECTING / OPEN / CLOSED). */
    status: 'connecting' | 'connected' | 'disconnected';
    /** Other clients connected to the same room (excluding us). */
    peers: YjsConnectionPeer[];
    /**
     * Last error message captured from the WebsocketProvider, when
     * the connection drops. Surfaced in the toolbar status row so
     * the user can see WHY the connection is offline (mixed-content
     * block, server crash, network drop, etc.).
     */
    lastError: string | null;
}

export interface UseYjsConnectionOptions {
    /** Display name for the current user (used in awareness + cursor colour). */
    userName: string;
    /** Hex colour for the current user's cursor. */
    userColor?: string;
    /**
     * If provided, the connection is opened against this room name
     * instead of `view:<viewId>`. Useful for tests that want to spin
     * up multiple docs in the same session.
     */
    roomName?: string;
    /**
     * Skip opening the WS connection entirely. The hook still returns
     * a fresh Y.Doc so callers can hydrate from the snapshot and run
     * a local-only editor.
     */
    disabled?: boolean;
}

export interface UseYjsConnectionResult extends YjsConnectionStatus {
    doc: Y.Doc;
    provider: WebsocketProvider | null;
    /** Force a reconnect (used after the snapshot hydrate handshake). */
    reconnect: () => void;
}

// ============================================
// HELPERS
// ============================================

/**
 * Resolve the WebSocket **server base URL** (no room name appended).
 *
 * The default (no env override) is the **same-origin** `/yws/` path,
 * which nginx proxies to the standalone `ywebsocket` container (see
 * `docker-compose.test.yml` + `frontend/nginx.conf`). Using the
 * same-origin path is critical because:
 *
 *   1. When the page is served over HTTPS, the browser blocks
 *      `ws://` connections as mixed-content. We must use `wss://`
 *      with the same scheme as the page.
 *
 *   2. In production the y-websocket service is reachable only via
 *      nginx (Traefik → frontend nginx → ywebsocket container).
 *      Connecting to `ws://localhost:1234` directly from a public
 *      domain would fail to resolve.
 *
 * `VITE_YJS_URL` is an opt-in override for non-standard setups
 * (e.g. a local Vite dev server pointing at a standalone y-websocket
 * on a different port).
 *
 * R16: this function intentionally returns ONLY the server base URL
 * (e.g. `wss://host/yws`). y-websocket's WebsocketProvider appends
 * `/${roomname}` automatically. If we pre-baked the roomname here,
 * the final URL would be `/yws/<room>/<room>`, which fails
 * end-to-end (see the file-level comment for details).
 */
function resolveWebSocketBaseUrl(): string {
    if (typeof window === 'undefined') {
        // SSR / tests: return an empty string so unit tests don't
        // crash. The provider is never opened in this branch.
        return '';
    }
    const fromEnv = (import.meta.env.VITE_YJS_URL as string | undefined) || '';
    if (fromEnv) {
        // Explicit override — strip any trailing slash so the
        // room append produces `/<room>` rather than `//<room>`.
        return fromEnv.replace(/\/+$/, '');
    }
    // Same-origin default. `window.location.protocol` already
    // includes the trailing colon (e.g. 'https:').
    const isHttps = window.location.protocol === 'https:';
    const scheme = isHttps ? 'wss' : 'ws';
    const host = window.location.host;
    return `${scheme}://${host}/yws`;
}

const PEER_COLORS = [
    '#f87171', '#fb923c', '#fbbf24', '#a3e635', '#34d399',
    '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#fb7185',
];

function pickPeerColor(seed: number): string {
    return PEER_COLORS[Math.abs(seed) % PEER_COLORS.length] || '#60a5fa';
}

// ============================================
// HOOK
// ============================================

export function useYjsConnection(
    viewId: string | null | undefined,
    options: UseYjsConnectionOptions
): UseYjsConnectionResult {
    const roomName = options.roomName || (viewId ? `view:${viewId}` : null);

    // The Y.Doc survives tab switches within the same view — we
    // return the same instance unless the viewId actually changes.
    // (When the user switches tabs, the parent unmounts this hook
    // and creates a fresh one.)
    const doc = useMemo(() => new Y.Doc(), [viewId]);

    const providerRef = useRef<WebsocketProvider | null>(null);
    // R19 (t60) — bug 4 fix: the previous version exposed
    // `provider: providerRef.current` from the hook return. Refs
    // are mutable but don't trigger a re-render, so the consumer
    // `DocumentEditor` always saw `provider === null` on the
    // FIRST render (the WebsocketProvider is created inside
    // `useEffect`, which runs AFTER render). The CollabCursor
    // extension therefore never made it into the editor's
    // extension list — the local user saw the editable-text
    // caret but never the collaborative cursor bar, AND had no
    // way to be shown to other peers either. Mirror `provider`
    // into state so the first re-render after the effect lands
    // pushes the live instance into the editor config.
    const [provider, setProvider] = useState<WebsocketProvider | null>(null);
    const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
    const [peers, setPeers] = useState<YjsConnectionPeer[]>([]);
    const [lastError, setLastError] = useState<string | null>(null);

    // Reset connection state on viewId / disable change.
    useEffect(() => {
        // Always clear any existing provider first so we never leak
        // sockets across viewId changes.
        if (providerRef.current) {
            providerRef.current.destroy();
            providerRef.current = null;
        }
        setProvider(null);
        setStatus(options.disabled ? 'disconnected' : 'connecting');
        setPeers([]);
        setLastError(null);

        if (options.disabled || !roomName) return;

        const baseUrl = resolveWebSocketBaseUrl();

        const provider = new WebsocketProvider(baseUrl, roomName, doc, {
            connect: true,
        });
        providerRef.current = provider;
        // R19 (t60) bug 4 fix: push the live provider into React
        // state so consumers (DocumentEditor) re-render with the
        // real instance and CollaborationCursor actually attaches.
        // Without this the editor is built once with `provider =
        // null` and the cursor extension is missing for the life
        // of the editor.
        setProvider(provider);

        // Set our awareness state so other peers see our cursor.
        provider.awareness.setLocalStateField('user', {
            name: options.userName,
            color: options.userColor || pickPeerColor(doc.clientID),
        });

        const handleStatus = (event: { status: 'connecting' | 'connected' | 'disconnected' }) => {
            setStatus(event.status);
            if (event.status === 'connected') {
                setLastError(null);
            }
        };
        provider.on('status', handleStatus);

        // y-websocket v2 emits a `connection-error` event when the WS
        // handshake fails (server unreachable, mixed-content block,
        // DNS failure, etc.). We capture the message so the toolbar
        // can surface it instead of a generic "Offline".
        const handleConnectionError = (event: Event) => {
            const message =
                (event as { message?: string }).message ||
                (event as { reason?: string }).reason ||
                'Connection failed';
            // eslint-disable-next-line no-console
            console.warn(`[yjs] connection-error (room=${roomName}): ${message}`);
            setLastError(message);
        };
        provider.on('connection-error', handleConnectionError);

        const handleAwarenessChange = () => {
            const states = provider.awareness.getStates();
            const list: YjsConnectionPeer[] = [];
            for (const [clientId, state] of states.entries()) {
                if (clientId === doc.clientID) continue;
                const user = (state as { user?: { name?: string; color?: string } }).user;
                if (!user) continue;
                list.push({
                    clientId,
                    name: user.name || 'Anonymous',
                    color: user.color || pickPeerColor(clientId),
                });
            }
            setPeers(list);
        };
        provider.awareness.on('change', handleAwarenessChange);

        return () => {
            provider.off('status', handleStatus);
            provider.off('connection-error', handleConnectionError);
            provider.awareness.off('change', handleAwarenessChange);
            provider.destroy();
            providerRef.current = null;
            setProvider(null);
        };
    }, [viewId, roomName, options.disabled, options.userName, options.userColor, doc]);

    return {
        doc,
        provider,
        status,
        peers,
        lastError,
        reconnect: () => {
            if (providerRef.current) {
                providerRef.current.disconnect();
                providerRef.current.connect();
            }
        },
    };
}